import { useCallback, useEffect, useState } from 'react'
import { apiRequest, ApiError } from './api'

export interface Resource<T> {
  data: T | null
  error: ApiError | Error | null
  loading: boolean
  /** Explicit user-initiated refresh. There is no automatic polling anywhere in this
   *  app on purpose: `GET /teacher/students/{id}` and the group roster each write a
   *  row to `student_access_log`, and a 30-second auto-refresh would turn the record
   *  of "which adult opened my child's card" into noise
   *  (27-teacher-api-handoff.md §2). */
  refresh: () => void
}

/**
 * Fetches on mount and whenever `path` changes — i.e. on navigation — and not
 * otherwise. Deliberately small: the alternative was a cache library, and a cache is
 * the wrong shape for endpoints that are audited on every read.
 */
export function useResource<T>(path: string | null): Resource<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<ApiError | Error | null>(null)
  const [loading, setLoading] = useState(path !== null)
  const [nonce, setNonce] = useState(0)

  const refresh = useCallback(() => setNonce((n) => n + 1), [])

  useEffect(() => {
    if (!path) {
      setData(null)
      setError(null)
      setLoading(false)
      return
    }

    const controller = new AbortController()
    let cancelled = false

    setLoading(true)
    void (async () => {
      try {
        const result = await apiRequest<T>(path, { signal: controller.signal })
        if (!cancelled) {
          setData(result)
          setError(null)
        }
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return
        setData(null)
        setError(caught instanceof Error ? caught : new Error(String(caught)))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [path, nonce])

  return { data, error, loading, refresh }
}
